import * as Rollbar from "rollbar";
import { NgModule, ErrorHandler } from "@angular/core";
import * as Highcharts from "highcharts";
import { BrowserModule, Title } from "@angular/platform-browser";
import { HttpClientModule } from "@angular/common/http";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { StoreModule } from "@ngrx/store";
import { StoreDevtoolsModule } from "@ngrx/store-devtools";
import { EffectsModule } from "@ngrx/effects";
import { environment } from "../environments/environment";

import { HighchartsChartModule } from "highcharts-angular";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TooltipModule } from "ngx-bootstrap/tooltip";

import { NgSelectModule } from "@ng-select/ng-select";
import { ScrollingModule } from "@angular/cdk/scrolling";
import { Angulartics2Module } from "angulartics2";
import { Angulartics2GoogleAnalytics } from "angulartics2/ga";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatDialogModule } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import {
  MatPaginatorModule,
  MatPaginatorIntl,
} from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSortModule } from "@angular/material/sort";

import { routing } from "./dashboard.routes";
import { WindowRef } from "./window/WindowRef";

import { LoginGuard } from "./LoginGuard";
import { RestrictedUserGuard } from "./RestrictedUserGuard";
import { NonAdminUserGuard } from "./NonAdminUserGuard";

import { SchoolEffects } from "./store/schools/effects";
import { GoalEffects } from "./store/goals/effects";
import { MetricEffects } from "./store/metrics/effects";
import { DistrictEffects } from "./store/district/effects";
import { StudentEffects } from "./store/students/effects";
import { goals } from "./store/goals/reducer";
import { schools } from "./store/schools/reducer";
import { metrics } from "./store/metrics/reducer";
import { district } from "./store/district/reducer";
import { students } from "./store/students/reducer";
import { getInitialState } from "./store/appState";

import { AppComponent } from "./app.component";
import { HomeComponent } from "./home/home.component";
import { NavbarComponent } from "./dashboard-elements/navbar/navbar.component";
import { SchoolProfileComponent } from "./school-profile/school-profile.component";
import { StudentProfileComponent } from "./student-profile/student-profile.component";
import { SchoolComparisonComponent } from "./school-comparison/school-comparison.component";
import { StudentComparisonComponent } from "./student-comparison/student-comparison.component";
import { PanelComponent } from "./dashboard-elements/panel/panel.component";
import { TileComponent } from "./dashboard-elements/tile/tile.component";
import { StackedColumnComponent } from "./charts/stacked-column/stacked-column.component";
import { LineComponent } from "./charts/line/line.component";
import { OverallComponent } from "./charts/overall/overall.component";
import { HorizontalBarComponent } from "./charts/horizontal-bar/horizontal-bar.component";
import { RankTableComponent } from "./charts/rank-table/rank-table.component";
import { ViewSelectButtonsComponent } from "./dashboard-elements/view-select-buttons/view-select-buttons.component";

import { KpiService } from "./kpi/kpi.service";
import { JobStatusService } from "./jobStatus/jobStatus.service";
import { DashboardDisplayService } from "./dashboardDisplay/dashboardDisplay.service";
import { GroupingService } from "./grouping/grouping.service";
import { BarComponent } from "./charts/bar/bar.component";
import { StudentInfoPanelComponent } from "./dashboard-elements/student-info-panel/student-info-panel.component";
import { MessagingComponent } from "./dashboard-elements/messaging/messaging.component";
import { SchoolCompTableComponent } from "./dashboard-elements/school-comp-table/school-comp-table.component";
import { SchoolCompTableHeadComponent } from "./dashboard-elements/school-comp-table-head/school-comp-table-head.component";
import { SchoolCompTableBodyComponent } from "./dashboard-elements/school-comp-table-body/school-comp-table-body.component";
import { ValueTooltipComponent } from "./dashboard-elements/value-tooltip/value-tooltip.component";
import { CompOverallValueTooltipComponent } from "./dashboard-elements/comp-overall-value-tooltip/comp-overall-value-tooltip.component";
import { TitleTooltipComponent } from "./dashboard-elements/title-tooltip/title-tooltip.component";
import { MetricSettingsComponent } from "./metric-settings/metric-settings.component";

import { EllipsisPipe } from "./dashboard-elements/pipes/ellipsis-pipe";
import { UserDataFilterPipe } from "./dashboard-elements/pipes/user-data-filter";
import { RemoveUnderscorePipe } from "./dashboard-elements/pipes/remove-underscore";
import { CommaSeparatedPipe } from "./dashboard-elements/pipes/comma-separated";
import { FilterPipe } from "./dashboard-elements/pipes/filter-pipe";

import { StickDirective } from "./dashboard-elements/directives/stick/stick.directive";
import { ScrollDirective } from "./dashboard-elements/directives/scroll/scroll.directive";

import { ArrowScrollForTablesComponent } from "./dashboard-elements/arrow-scroll-for-tables/arrow-scroll-for-tables.component";
import { MatPaginatorIntlCustom } from "./classes/paginator";

import { RollbarErrorHandler } from "./error-handler";
import { RollbarService } from "./error-handler";
import { SettingsNavComponent } from "./dashboard-elements/settings-nav/settings-nav.component";
import { SettingsNavElementsComponent } from "./dashboard-elements/settings-nav-elements/settings-nav-elements.component";
import { MetricSettingsEditorComponent } from "./dashboard-elements/metric-settings-editor/metric-settings-editor.component";
import { MetricSettingsEditorSideNavComponent } from "./dashboard-elements/metric-settings-editor-side-nav/metric-settings-editor-side-nav.component";
import { MetricSettingsEditorFormComponent } from "./dashboard-elements/metric-settings-editor-form/metric-settings-editor-form.component";
import { TextAreaInputElementComponent } from "./dashboard-elements/text-area-input-element/text-area-input-element.component";
import { EditGroupModalComponent } from "./dashboard-elements/edit-group-modal/edit-group-modal.component";
import { MetricSettingsPreviewComponent } from "./dashboard-elements/metric-settings-preview/metric-settings-preview.component";
import { MetricSettingsTooltipComponent } from "./dashboard-elements/metric-settings-tooltip/metric-settings-tooltip.component";
import { MetricSettingsTileComponent } from "./dashboard-elements/metric-settings-tile/metric-settings-tile.component";
import { DashboardConfigSettingsComponent } from "./dashboard-config-settings/dashboard-config-settings.component";
import { DashboardConfigBodyComponent } from "./dashboard-elements/dashboard-config-body/dashboard-config-body.component";
import { DemographicBreakdownsComponent } from "./demographic-breakdowns/demographic-breakdowns.component";
import { SchoolConfigurationsComponent } from "./school-configurations/school-configurations.component";
import { MetricConfigurationsComponent } from "./metric-configurations/metric-configurations.component";
import { GroupingSideNavComponent } from "./dashboard-elements/grouping-side-nav/grouping-side-nav.component";
import { GroupComponent } from "./dashboard-elements/group/group.component";
import { GroupingBodyComponent } from "./dashboard-elements/grouping-body/grouping-body.component";
import { MovePanelComponent } from "./dashboard-elements/move-panel/move-panel.component";
import { FooterComponent } from "./dashboard-elements/footer/footer.component";
import { VisibilityToggleComponent } from "./dashboard-elements/visibility-toggle/visibility-toggle.component";
import { GroupingBodyTitleComponent } from "./dashboard-elements/grouping-body-title/grouping-body-title.component";
import { UsageComponent } from "./usage/usage.component";
import { TileTitleComponent } from "./dashboard-elements/tile-title/tile-title.component";
import { SchoolUsageTableComponent } from "./dashboard-elements/school-usage-table/school-usage-table.component";
import { SparklineComponent } from "./charts/sparkline/sparkline.component";
import { OverallValueTooltipComponent } from "./dashboard-elements/overall-value-tooltip/overall-value-tooltip.component";
import { UserLoginsTableComponent } from "./dashboard-elements/user-logins-table/user-logins-table.component";

import { SchoolCompTableFixedColComponent } from "./dashboard-elements/school-comp-table-fixed-col/school-comp-table-fixed-col.component";
import { SchoolCompTableFixedColHeaderComponent } from "./dashboard-elements/school-comp-table-fixed-col-header/school-comp-table-fixed-col-header.component";
import { StudentCompTableFixedColComponent } from "./dashboard-elements/student-comp-table-fixed-col/student-comp-table-fixed-col.component";
import { StudentCompTableFixedColHeaderComponent } from "./dashboard-elements/student-comp-table-fixed-col-header/student-comp-table-fixed-col-header.component";
import { StudentCompTableComponent } from "./dashboard-elements/student-comp-table/student-comp-table.component";
import { StudentCompTableBodyComponent } from "./dashboard-elements/student-comp-table-body/student-comp-table-body.component";
import { StudentCompTableHeadComponent } from "./dashboard-elements/student-comp-table-head/student-comp-table-head.component";
import { SortRadioBtnsComponent } from "./dashboard-elements/sort-radio-btns/sort-radio-btns.component";
import { FilterSelectAndInputsComponent } from "./dashboard-elements/filter-select-and-inputs/filter-select-and-inputs.component";
import { SortFilterImagesComponent } from "./dashboard-elements/sort-filter-images/sort-filter-images.component";
import { StudentSortFiltersDisplayComponent } from "./dashboard-elements/student-sort-filters-display/student-sort-filters-display.component";
import { ListSettingsModalComponent } from "./dashboard-elements/list-settings-modal/list-settings-modal.component";
import { ListSettingsSidebarComponent } from "./dashboard-elements/list-settings-sidebar/list-settings-sidebar.component";
import { ListSettingsDetailsPanelComponent } from "./dashboard-elements/list-settings-details-panel/list-settings-details-panel.component";
import { DeleteViewConfirmationComponent } from "./dashboard-elements/delete-view-confirmation/delete-view-confirmation.component";
import { TopNavBtnsComponent } from "./dashboard-elements/top-nav-btns/top-nav-btns.component";
import { TopNavBtnComponent } from "./dashboard-elements/top-nav-btn/top-nav-btn.component";
import { TopNavDropdownComponent } from "./dashboard-elements/top-nav-dropdown/top-nav-dropdown.component";
import { TopNavBtnInnerComponent } from "./dashboard-elements/top-nav-btn-inner/top-nav-btn-inner.component";
import { NavbarHamburgerComponent } from "./dashboard-elements/navbar-hamburger/navbar-hamburger.component";
import { OfficialEnrollmentRulesComponent } from "./dashboard-elements/official-enrollment-rules/official-enrollment-rules.component";
import { MinDaysEnrolledInputComponent } from "./dashboard-elements/min-days-enrolled-input/min-days-enrolled-input.component";
import { OfficialEnrollmentRulesPanelComponent } from "./dashboard-elements/official-enrollment-rules-panel/official-enrollment-rules-panel.component";
import { EnrollmentWindowsTableComponent } from "./dashboard-elements/enrollment-windows-table/enrollment-windows-table.component";
import { NoShowsInputComponent } from "./dashboard-elements/no-shows-input/no-shows-input.component";
import { MetricSettingsGoalsComponent } from "./dashboard-elements/metric-settings-goals/metric-settings-goals.component";
import { GoalRowComponent } from "./dashboard-elements/goal-row/goal-row.component";
import { SchoolLevelsSelectComponent } from "./dashboard-elements/school-levels-select/school-levels-select.component";
import { GoalRowInputsComponent } from "./dashboard-elements/goal-row-inputs/goal-row-inputs.component";
import { GoalFilterRowComponent } from "./dashboard-elements/goal-filter-row/goal-filter-row.component";
import { OtherReportsUrlConfigComponent } from "./dashboard-elements/other-reports-url-config/other-reports-url-config.component";
import { ViewCriteriaComponent } from "./dashboard-elements/view-criteria/view-criteria.component";
import { MultiselectWithHelpersComponent } from "./dashboard-elements/multiselect-with-helpers/multiselect-with-helpers.component";
import { UnsavedChangesConfirmModalComponent } from "./dashboard-elements/unsaved-changes-confirm-modal/unsaved-changes-confirm-modal.component";
import { ListSettingsSublistComponent } from "./dashboard-elements/list-settings-sublist/list-settings-sublist.component";
import { GroupChipsComponent } from "./dashboard-elements/group-chips/group-chips.component";
import { GroupCriteriaComponent } from "./dashboard-elements/group-criteria/group-criteria.component";
import { GroupCriteriaDescriptionImagesComponent } from "./dashboard-elements/group-criteria-description-images/group-criteria-description-images.component";
import { GroupCriteriaDescriptionImageComponent } from "./dashboard-elements/group-criteria-description-image/group-criteria-description-image.component";
import { SetGroupCriteriaComponent } from "./dashboard-elements/set-group-criteria/set-group-criteria.component";
import { DisplayGroupCriteriaComponent } from "./dashboard-elements/display-group-criteria/display-group-criteria.component";
import { ListSettingsAboutPanelComponent } from "./dashboard-elements/list-settings-about-panel/list-settings-about-panel.component";
import { GroupCriteriaPanelDisplayComponent } from "./dashboard-elements/group-criteria-panel-display/group-criteria-panel-display.component";
import { CriteriaDisplayTextComponent } from "./dashboard-elements/criteria-display-text/criteria-display-text.component";
import { AddStudentsToGroupModalComponent } from "./dashboard-elements/add-students-to-group-modal/add-students-to-group-modal.component";
import { GroupStatusFiltersComponent } from "./dashboard-elements/group-status-filters/group-status-filters.component";
import { ExportStudentsToCsvModalComponent } from "./dashboard-elements/export-students-to-csv-modal/export-students-to-csv-modal.component";
import { DeepDiveLightboxComponent } from "./dashboard-elements/deep-dive-lightbox/deep-dive-lightbox.component";
import { LineExpandedComponent } from "./charts/line-expanded/line-expanded.component";
import { TimePeriodBtnGroupComponent } from "./dashboard-elements/time-period-btn-group/time-period-btn-group.component";
import { CheckboxMultiselectComponent } from "./dashboard-elements/checkbox-multiselect/checkbox-multiselect.component";
import { ChartIconButtonComponent } from "./dashboard-elements/chart-icon-button/chart-icon-button.component";
import { LightboxTableComponent } from "./dashboard-elements/lightbox-table/lightbox-table.component";
import { CalendarTileComponent } from "./charts/calendar-tile/calendar-tile.component";
import { CalendarTileTooltipComponent } from "./dashboard-elements/calendar-tile-tooltip/calendar-tile-tooltip.component";
import { MzInfoMessageComponent } from "./dashboard-elements/mz-info-message/mz-info-message.component";
import { CustomMatTableSortIconComponent } from "./dashboard-elements/custom-mat-table-sort-icon/custom-mat-table-sort-icon.component";
import { MzTableTooltipComponent } from "./dashboard-elements/mz-table-tooltip/mz-table-tooltip.component";
import { LeaveSZConfirmDialogComponent } from "./dashboard-elements/leave-sz-confirm-dialog/leave-sz-confirm-dialog.component";
import { NavbarAnnouncementComponent } from "./dashboard-elements/navbar-announcement/navbar-announcement.component";
import { CalendarTileLegendItemComponent } from "./dashboard-elements/calendar-tile-legend-item/calendar-tile-legend-item.component";
import { CalendarMonthToggleComponent } from "./dashboard-elements/calendar-month-toggle/calendar-month-toggle.component";
import { StackedColumnExpandedComponent } from "./charts/stacked-column-expanded/stacked-column-expanded.component";
import { MzTrendChartComponent } from "./dashboard-elements/mz-trend-chart/mz-trend-chart.component";
import { ExpandTileComponent } from "./dashboard-elements/expand-tile/expand-tile.component";
import { SparklineExtendedComponent } from "./charts/sparkline-extended/sparkline-extended.component";
import { ExpandTileTableComponent } from "./dashboard-elements/expand-tile-table/expand-tile-table.component";
import { ClickableBarComponent } from "./dashboard-elements/clickable-bar/clickable-bar.component";
import { ClickableBarTooltipComponent } from "./dashboard-elements/clickable-bar-tooltip/clickable-bar-tooltip.component";
import { VisibilitySettingsComponent } from "./dashboard-elements/visibility-settings/visibility-settings.component";
import { MetricSettingsBodyComponent } from "./dashboard-elements/metric-settings-body/metric-settings-body.component";
import { VisibilitySettingsTableComponent } from "./dashboard-elements/visibility-settings-table/visibility-settings-table.component";
import { GraduationPlanConfigurationComponent } from "./dashboard-elements/graduation-plan-configuration/graduation-plan-configuration.component";
import { MarketoFormComponent } from "./dashboard-elements/marketo-form/marketo-form.component";
import { LiveChatComponent } from "./dashboard-elements/live-chat/live-chat.component";
import { OurStudentsTooltipComponent } from "./dashboard-elements/our-students-tooltip/our-students-tooltip.component";
import { PanelHeaderComponent } from "./dashboard-elements/panel-header/panel-header.component";
import { LaunchDarklyService } from "./launchDarkly/launchDarkly.service";
import { PracticeDataUnavailableComponent } from "./dashboard-elements/practice-data-unavailable/practice-data-unavailable.component";
import { TileViewCarrouselComponent } from './dashboard-elements/tile-view-carrousel/tile-view-carrousel.component';

const rollbarConfig =
  environment.mode == "production"
    ? {
        accessToken: environment.rollbarAccessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
      }
    : {
        enabled: false,
      };

export function rollbarFactory() {
  return new Rollbar(rollbarConfig);
}

Highcharts.setOptions({
  lang: {
    thousandsSep: ",",
  },
  tooltip: {
    outside: true,
    style: { "z-index": 9999 },
  },
});
declare var require: any;
const HighchartsBoost = require("highcharts/modules/boost");
HighchartsBoost(Highcharts);

@NgModule({
  imports: [
    BrowserModule,
    HttpClientModule,
    routing,
    HighchartsChartModule,
    BsDropdownModule.forRoot(),
    NgSelectModule,
    TooltipModule.forRoot(),
    Angulartics2Module.forRoot(),
    MatButtonToggleModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatListModule,
    MatTooltipModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSelectModule,
    MatInputModule,
    MatCardModule,
    MatSlideToggleModule,
    MatTableModule,
    MatPaginatorModule,
    MatDialogModule,
    MatSnackBarModule,
    MatChipsModule,
    BrowserAnimationsModule,
    MatProgressSpinnerModule,
    MatSortModule,
    MatExpansionModule,
    ScrollingModule,
    StoreModule.forRoot(
      { goals, schools, metrics, district, students },
      { initialState: getInitialState }
    ),
    // Instrumentation must be imported after importing StoreModule (config is optional)
    environment.mode != "production"
      ? StoreDevtoolsModule.instrument({
          maxAge: 25, // Retains last 25 states
        })
      : [],
    EffectsModule.forRoot([
      GoalEffects,
      SchoolEffects,
      MetricEffects,
      DistrictEffects,
      StudentEffects,
    ]),
  ], // module dependencies
  declarations: [
    AppComponent,
    HomeComponent,
    NavbarComponent,
    SchoolProfileComponent,
    StudentProfileComponent,
    SchoolComparisonComponent,
    StudentComparisonComponent,
    PanelComponent,
    TileComponent,
    StackedColumnComponent,
    LineComponent,
    OverallComponent,
    HorizontalBarComponent,
    RankTableComponent,
    ViewSelectButtonsComponent,
    BarComponent,
    StudentInfoPanelComponent,
    MessagingComponent,
    SchoolCompTableComponent,
    SchoolCompTableHeadComponent,
    SchoolCompTableBodyComponent,
    ValueTooltipComponent,
    CompOverallValueTooltipComponent,
    TitleTooltipComponent,
    MetricSettingsComponent,
    SettingsNavComponent,
    SettingsNavElementsComponent,
    MetricSettingsEditorComponent,
    MetricSettingsEditorSideNavComponent,
    MetricSettingsEditorFormComponent,
    TextAreaInputElementComponent,
    EditGroupModalComponent,
    MetricSettingsPreviewComponent,
    MetricSettingsTooltipComponent,
    MetricSettingsTileComponent,
    EllipsisPipe,
    UserDataFilterPipe,
    RemoveUnderscorePipe,
    CommaSeparatedPipe,
    FilterPipe,
    DashboardConfigSettingsComponent,
    DashboardConfigBodyComponent,
    DemographicBreakdownsComponent,
    SchoolConfigurationsComponent,
    MetricConfigurationsComponent,
    GroupingSideNavComponent,
    GroupComponent,
    GroupingBodyComponent,
    MovePanelComponent,
    FooterComponent,
    VisibilityToggleComponent,
    GroupingBodyTitleComponent,
    UsageComponent,
    TileTitleComponent,
    SchoolUsageTableComponent,
    SparklineComponent,
    OverallValueTooltipComponent,
    UserLoginsTableComponent,
    SchoolCompTableFixedColComponent,
    SchoolCompTableFixedColHeaderComponent,
    StudentCompTableFixedColComponent,
    StudentCompTableFixedColHeaderComponent,
    StudentCompTableComponent,
    StudentCompTableBodyComponent,
    StudentCompTableHeadComponent,
    SortRadioBtnsComponent,
    FilterSelectAndInputsComponent,
    SortFilterImagesComponent,
    StudentSortFiltersDisplayComponent,
    ListSettingsModalComponent,
    ListSettingsSidebarComponent,
    ListSettingsDetailsPanelComponent,
    DeleteViewConfirmationComponent,
    TopNavBtnsComponent,
    TopNavBtnComponent,
    TopNavDropdownComponent,
    TopNavBtnInnerComponent,
    NavbarHamburgerComponent,
    OfficialEnrollmentRulesComponent,
    MinDaysEnrolledInputComponent,
    OfficialEnrollmentRulesPanelComponent,
    EnrollmentWindowsTableComponent,
    NoShowsInputComponent,
    MetricSettingsGoalsComponent,
    GoalRowComponent,
    SchoolLevelsSelectComponent,
    GoalRowInputsComponent,
    GoalFilterRowComponent,
    OtherReportsUrlConfigComponent,
    StickDirective,
    ScrollDirective,
    ViewCriteriaComponent,
    MultiselectWithHelpersComponent,
    UnsavedChangesConfirmModalComponent,
    ListSettingsSublistComponent,
    GroupChipsComponent,
    GroupCriteriaComponent,
    GroupCriteriaDescriptionImagesComponent,
    GroupCriteriaDescriptionImageComponent,
    SetGroupCriteriaComponent,
    DisplayGroupCriteriaComponent,
    ListSettingsAboutPanelComponent,
    GroupCriteriaPanelDisplayComponent,
    CriteriaDisplayTextComponent,
    AddStudentsToGroupModalComponent,
    GroupStatusFiltersComponent,
    ArrowScrollForTablesComponent,
    ExportStudentsToCsvModalComponent,
    DeepDiveLightboxComponent,
    LineExpandedComponent,
    TimePeriodBtnGroupComponent,
    CheckboxMultiselectComponent,
    ChartIconButtonComponent,
    LightboxTableComponent,
    CalendarTileComponent,
    CalendarTileTooltipComponent,
    MzInfoMessageComponent,
    CustomMatTableSortIconComponent,
    MzTableTooltipComponent,
    LeaveSZConfirmDialogComponent,
    NavbarAnnouncementComponent,
    CalendarTileLegendItemComponent,
    CalendarMonthToggleComponent,
    StackedColumnExpandedComponent,
    MzTrendChartComponent,
    ExpandTileComponent,
    SparklineExtendedComponent,
    ExpandTileTableComponent,
    ClickableBarComponent,
    ClickableBarTooltipComponent,
    VisibilitySettingsComponent,
    MetricSettingsBodyComponent,
    VisibilitySettingsTableComponent,
    GraduationPlanConfigurationComponent,
    MarketoFormComponent,
    LiveChatComponent,
    OurStudentsTooltipComponent,
    PanelHeaderComponent,
    PracticeDataUnavailableComponent,
    TileViewCarrouselComponent,
  ], // components and directives
  bootstrap: [AppComponent], // root component
  providers: [
    KpiService,
    DashboardDisplayService,
    GroupingService,
    JobStatusService,
    Title,
    WindowRef,
    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlCustom },
    LoginGuard,
    RestrictedUserGuard,
    NonAdminUserGuard,
    LaunchDarklyService,
  ],
  entryComponents: [
    EditGroupModalComponent,
    ListSettingsModalComponent,
    UnsavedChangesConfirmModalComponent,
    AddStudentsToGroupModalComponent,
    ExportStudentsToCsvModalComponent,
    DeepDiveLightboxComponent,
    LeaveSZConfirmDialogComponent,
  ], // services
})
export class AppModule {}
